"use client";

import type { ReactNode } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export type FormFieldProps = {
  /** Static label rendered above the input. */
  label?: ReactNode;
  /** Helper text below the input. Pass the error message through this when invalid. */
  helperText?: ReactNode;
  /** True when the field is in an error state — colors the label and helper. */
  error?: boolean;
  /** Renders an asterisk on the label and is forwarded to the input. */
  required?: boolean;
  /**
   * Suppresses the label asterisk without dropping `required` from the input,
   * so assistive tech still announces the field as required. For surfaces
   * where every field is required and the marker is visual noise.
   */
  hideRequiredMarker?: boolean;
  /** Wires up `htmlFor` on the label so clicking it focuses the input. */
  htmlFor?: string;
  /** id on the label element. Required for inputs that need aria-labelledby (e.g. MUI Select). */
  labelId?: string;
  /** The actual input. */
  children: ReactNode;
};

export const FormField = ({
  label,
  helperText,
  error,
  required,
  hideRequiredMarker,
  htmlFor,
  labelId,
  children,
}: FormFieldProps) => (
  <Stack spacing={0.5}>
    {label && (
      <Typography
        id={labelId}
        component="label"
        htmlFor={htmlFor}
        variant="bodyMdMedium"
        color={error ? "error" : "text.primary"}
      >
        {label}
        {required && !hideRequiredMarker && (
          <Typography component="span" color="error" aria-hidden="true">
            {" *"}
          </Typography>
        )}
      </Typography>
    )}
    {children}
    {helperText && (
      <Typography
        variant="bodyXsRegular"
        color={error ? "error" : "text.secondary"}
      >
        {helperText}
      </Typography>
    )}
  </Stack>
);
